/**
 * Reported decisions and public condemnation records in which a court stated
 * dollar figures for the value impact of an easement or right-of-way.
 *
 * Every entry is transcribed from a free, publicly available opinion. Figures
 * are the amounts the court itself recited; they are historical and are NOT a
 * substitute for a current appraisal. Nothing here is fabricated -- leads that
 * could not be verified against a retrievable opinion were left out.
 */

export type CaseEasementType =
  | "transmission"
  | "pipeline"
  | "drainage"
  | "access"
  | "conservation";

export interface EasementValuationCase {
  id: string;
  caseName: string;
  year: number;
  court: string;
  /** Two-letter state code, or "US" for a federal multi-state decision. */
  stateCode: string;
  stateName: string;
  easementType: CaseEasementType;
  corridorDescription: string;
  /** Value the court assigned to the land actually encumbered, in dollars. */
  landTakenValue: number | null;
  /** Damage to the remainder ("severance" or "consequential" damages). */
  severanceDamages: number | null;
  /** Total just compensation stated by the court. */
  totalAward: number | null;
  /** Before-condition value of the whole property, where stated. */
  beforeValue: number | null;
  holding: string;
  sourceUrl: string;
}

export const EASEMENT_VALUATION_CASES: EasementValuationCase[] = [
  {
    id: "swlemc-beck-1974",
    caseName: "Southwest Louisiana Electric Membership Corp. v. Beck",
    year: 1974,
    court: "Louisiana Court of Appeal, Third Circuit",
    stateCode: "LA",
    stateName: "Louisiana",
    easementType: "transmission",
    corridorDescription:
      "100-foot-wide H-frame transmission servitude across a 14.74-acre tract; 0.946 acres taken, including a 0.676-acre pocket left isolated",
    landTakenValue: 3000,
    severanceDamages: 5268,
    totalAward: 8268,
    beforeValue: null,
    holding:
      "The trial court awarded $3,000 for the land taken and $1,656 in severance damages for an isolated pocket; the court of appeal more than tripled the total severance award by adding $3,612 for the remainder of the tract, finding the nearby transmission line reduced its market value.",
    sourceUrl: "https://hallapproved.com/la/cases/appeals/1974/1884960/",
  },
  {
    id: "gravity-drainage-key-1958",
    caseName: "Gravity Drainage District No. 1 v. Key (Key parcel)",
    year: 1958,
    court: "Supreme Court of Louisiana",
    stateCode: "LA",
    stateName: "Louisiana",
    easementType: "drainage",
    corridorDescription: "35-foot-wide drainage ditch servitude; 0.384 acres taken",
    landTakenValue: 3330,
    severanceDamages: 6588,
    totalAward: 13518,
    beforeValue: null,
    holding:
      "Industrial-zoned land was valued at $10,000 an acre, and the court added a separate $3,600 culvert allowance so the owner kept rail-spur access.",
    sourceUrl: "https://hallapproved.com/la/cases/supreme/1958/1912812/",
  },
  {
    id: "gravity-drainage-key-1958-moss",
    caseName: "Gravity Drainage District No. 1 v. Key (Moss parcel)",
    year: 1958,
    court: "Supreme Court of Louisiana",
    stateCode: "LA",
    stateName: "Louisiana",
    easementType: "drainage",
    corridorDescription: "35-foot-wide drainage ditch servitude; 0.222 acres taken",
    landTakenValue: 4551,
    severanceDamages: 5416,
    totalAward: 13567,
    beforeValue: null,
    holding:
      "Same taking, different parcel: land here was worth $20,500 an acre, the remainder lost $4,000 an acre, and a $3,600 culvert allowance was again added.",
    sourceUrl: "https://hallapproved.com/la/cases/supreme/1958/1912812/",
  },
  {
    id: "coastal-transmission-lejeune-1962",
    caseName: "Coastal Transmission Corp. v. Lejeune",
    year: 1962,
    court: "Louisiana Court of Appeal, Third Circuit",
    stateCode: "LA",
    stateName: "Louisiana",
    easementType: "pipeline",
    corridorDescription:
      "Gas pipeline servitude roughly 1,300 feet long and 10 to 30 feet wide, about 0.9 acres, across a 15-acre tract",
    landTakenValue: 863,
    severanceDamages: 717,
    totalAward: 2709,
    beforeValue: null,
    holding:
      "Land was valued at $1,000 an acre, and damage to the remainder was tied to the strip nearest the pipe rather than a flat percentage of the whole property, plus $1,128 for destroyed timber.",
    sourceUrl: "https://hallapproved.com/la/cases/appeals/1962/1844363/",
  },
  {
    id: "swlemc-duhon-1975",
    caseName: "Southwest Louisiana Electric Membership Corp. v. Duhon (Duhon parcel)",
    year: 1975,
    court: "Louisiana Court of Appeal, Third Circuit",
    stateCode: "LA",
    stateName: "Louisiana",
    easementType: "transmission",
    corridorDescription:
      "100-foot-wide, 1,264-foot-long transmission servitude; 2.89 acres taken from a 21.97-acre tract",
    landTakenValue: 8780,
    severanceDamages: 9525,
    totalAward: 18305,
    beforeValue: null,
    holding:
      "The court accepted a graduated value -- full fee value for the road frontage, 80 percent for the rear -- and added $3,000 purely for the line's proximity to a house.",
    sourceUrl: "https://hallapproved.com/la/cases/appeals/1975/1905641/",
  },
  {
    id: "swlemc-duhon-1975-broussard",
    caseName: "Southwest Louisiana Electric Membership Corp. v. Duhon (Broussard parcel)",
    year: 1975,
    court: "Louisiana Court of Appeal, Third Circuit",
    stateCode: "LA",
    stateName: "Louisiana",
    easementType: "transmission",
    corridorDescription:
      "Same transmission line; a 10-foot-wide, 0.045-acre sliver taken from an adjoining 23-acre tract",
    landTakenValue: 108,
    severanceDamages: 6525,
    totalAward: 6633,
    beforeValue: null,
    holding:
      "A tiny strip produced a very large remainder award, because the line crossing the boundary undercut the neighbor's ability to subdivide.",
    sourceUrl: "https://hallapproved.com/la/cases/appeals/1975/1905641/",
  },
  {
    id: "pge-hufford-1957",
    caseName: "Pacific Gas & Electric Co. v. Hufford",
    year: 1957,
    court: "Supreme Court of California",
    stateCode: "CA",
    stateName: "California",
    easementType: "transmission",
    corridorDescription:
      "80-foot-wide transmission easement running 5,171 feet, plus a 20-foot road easement -- about 13.1 acres across a 1,179-acre ranch",
    landTakenValue: 1000,
    severanceDamages: 12500,
    totalAward: 13500,
    beforeValue: null,
    holding:
      "The strip itself was worth little because the rancher kept grazing it, yet the jury could still award far more for harm to the rest of the ranch without double counting.",
    sourceUrl: "https://hallapproved.com/ca/cases/supreme/1957/1132962/",
  },
  {
    id: "sac-sj-drainage-goehring-1970",
    caseName: "Sacramento & San Joaquin Drainage District v. Goehring",
    year: 1970,
    court: "California Court of Appeal, Third District",
    stateCode: "CA",
    stateName: "California",
    easementType: "drainage",
    corridorDescription:
      "20-foot-wide temporary construction and access easements plus flood-control interests across a 140-acre parcel",
    landTakenValue: 910,
    severanceDamages: null,
    totalAward: 910,
    beforeValue: null,
    holding:
      "The $900 award for road damage was upheld, but the court of appeal reversed the $2,250 severance-damages award because the owner's opinion on lost value rested on a claimed 'right' of creek access that was not legally enforceable.",
    sourceUrl: "https://hallapproved.com/ca/cases/appeals/1970/2162210/",
  },
  {
    id: "mvp-8-37-acres-2024",
    caseName: "Mountain Valley Pipeline, LLC v. 8.37 Acres of Land (Terry)",
    year: 2024,
    court: "U.S. Court of Appeals for the Fourth Circuit",
    stateCode: "VA",
    stateName: "Virginia",
    easementType: "pipeline",
    corridorDescription:
      "Permanent and temporary natural-gas pipeline easements totaling 8.37 acres across a 560-acre farm",
    landTakenValue: null,
    severanceDamages: null,
    totalAward: 523327,
    beforeValue: null,
    holding:
      "The appeals court restored the jury's full $523,327 award after the trial judge had cut it in half, holding the jury was entitled to credit the landowner's appraiser.",
    sourceUrl: "https://www.ca4.uscourts.gov/opinions/231532.P.pdf",
  },
  {
    id: "columbia-gas-76-acres-boyce-2017",
    caseName: "Columbia Gas Transmission, LLC v. 76 Acres (Boyce)",
    year: 2017,
    court: "U.S. Court of Appeals for the Fourth Circuit",
    stateCode: "MD",
    stateName: "Maryland",
    easementType: "pipeline",
    corridorDescription:
      "Permanent 50-foot corridor of 0.68 acres, plus temporary construction easements of 0.38 and 0.32 acres",
    landTakenValue: 29216,
    severanceDamages: 167011,
    totalAward: 246331,
    beforeValue: null,
    holding:
      "The jury priced the permanent strip, the temporary work space, and the harm to the rest of the farm separately; only the temporary-easement figure was sent back for retrial.",
    sourceUrl: "https://hallapproved.com/us/cases/ca4/2017/4408771/",
  },
  {
    id: "columbia-gas-76-acres-boyer-2017",
    caseName: "Columbia Gas Transmission, LLC v. 76 Acres (Boyer)",
    year: 2017,
    court: "U.S. Court of Appeals for the Fourth Circuit",
    stateCode: "MD",
    stateName: "Maryland",
    easementType: "pipeline",
    corridorDescription:
      "Permanent easement of 0.67 acres, access-road use of 0.09 acres, temporary easements of 0.29 and 0.12 acres",
    landTakenValue: 27872,
    severanceDamages: 93948,
    totalAward: 127964,
    beforeValue: null,
    holding:
      "Damage to the remainder came to more than three times the value of the strip itself, and the award was affirmed in full.",
    sourceUrl: "https://hallapproved.com/us/cases/ca4/2017/4408771/",
  },
  {
    id: "columbia-gas-76-acres-kenney-2017",
    caseName: "Columbia Gas Transmission, LLC v. 76 Acres (Kenney)",
    year: 2017,
    court: "U.S. Court of Appeals for the Fourth Circuit",
    stateCode: "MD",
    stateName: "Maryland",
    easementType: "pipeline",
    corridorDescription:
      "Permanent easement of 0.9965 acres, temporary construction easement of 0.4664 acres, temporary license of 0.0101 acres",
    landTakenValue: 43268,
    severanceDamages: 130668,
    totalAward: 181575,
    beforeValue: null,
    holding:
      "The award itemized a $7,639 temporary easement, and the court revived the owner's separate claim for the value of timber that had been cut.",
    sourceUrl: "https://hallapproved.com/us/cases/ca4/2017/4408771/",
  },
  {
    id: "tennessee-gas-king-arthur-2016",
    caseName: "Tennessee Gas Pipeline Co. v. Permanent Easement for 7.053 Acres",
    year: 2016,
    court: "U.S. District Court, Middle District of Pennsylvania",
    stateCode: "PA",
    stateName: "Pennsylvania",
    easementType: "pipeline",
    corridorDescription:
      "7.053-acre permanent easement, 1.709-acre overlay easement, and 8.551 acres of temporary easements across a 975-acre, 18-parcel holding",
    landTakenValue: null,
    severanceDamages: 44440,
    totalAward: null,
    beforeValue: 4029000,
    holding:
      "The court sorted through the competing appraisals to decide which categories of loss could be claimed at all; it set out the figures rather than fixing a final award.",
    sourceUrl:
      "https://storage.courtlistener.com/recap/gov.uscourts.pamd.90288/gov.uscourts.pamd.90288.43.0.pdf",
  },
  {
    id: "abbas-franklin-county-2025-abbas",
    caseName: "Abbas v. Franklin County Board of Supervisors (Abbas property)",
    year: 2025,
    court: "Supreme Court of Iowa",
    stateCode: "IA",
    stateName: "Iowa",
    easementType: "drainage",
    corridorDescription:
      "1.80 acres of additional open drainage-ditch right-of-way beyond the district's historical easement",
    landTakenValue: 13847,
    severanceDamages: null,
    totalAward: 13847,
    beforeValue: null,
    holding:
      "The court reversed the $91,189 remainder award: the rebuilt ditch did not make farming any harder than the easement that already existed, so only the new acreage was compensable.",
    sourceUrl: "https://www.iowacourts.gov/courtcases/20701/embed/SupremeCourtOpinion",
  },
  {
    id: "abbas-franklin-county-2025-hanson",
    caseName: "Abbas v. Franklin County Board of Supervisors (Hanson property)",
    year: 2025,
    court: "Supreme Court of Iowa",
    stateCode: "IA",
    stateName: "Iowa",
    easementType: "drainage",
    corridorDescription:
      "1.46 acres of additional open drainage-ditch right-of-way beyond the district's historical easement",
    landTakenValue: 13073,
    severanceDamages: null,
    totalAward: 13073,
    beforeValue: null,
    holding:
      "Same reasoning as the neighboring farm -- the $162,003 damage award to the remainder was reversed because the work stayed inside the district's existing rights.",
    sourceUrl: "https://www.iowacourts.gov/courtcases/20701/embed/SupremeCourtOpinion",
  },
  {
    id: "abbas-franklin-county-2025-reid-meyer",
    caseName: "Abbas v. Franklin County Board of Supervisors (Reid-Meyer property)",
    year: 2025,
    court: "Supreme Court of Iowa",
    stateCode: "IA",
    stateName: "Iowa",
    easementType: "drainage",
    corridorDescription:
      "1.32 acres of additional right-of-way, plus a 4.01-acre parcel left landlocked by the rebuilt open ditch",
    landTakenValue: 11302,
    severanceDamages: 36915,
    totalAward: 48217,
    beforeValue: null,
    holding:
      "Here the remainder damages stuck, but only for the 4.01 acres that became landlocked -- an impairment that went beyond what the district already had the right to do.",
    sourceUrl: "https://www.iowacourts.gov/courtcases/20701/embed/SupremeCourtOpinion",
  },
];

export const CASE_TYPE_LABELS: Record<CaseEasementType, string> = {
  transmission: "Power line",
  pipeline: "Pipeline",
  drainage: "Drainage",
  access: "Access / driveway",
  conservation: "Conservation",
};

export function casesForType(type: CaseEasementType): EasementValuationCase[] {
  return EASEMENT_VALUATION_CASES.filter((entry) => entry.easementType === type);
}

export function casesForState(stateCode: string): EasementValuationCase[] {
  const code = stateCode.trim().toUpperCase();
  return EASEMENT_VALUATION_CASES.filter((entry) => entry.stateCode === code);
}

/**
 * Share of total compensation attributable to damage to the remainder, for the
 * cases that report both figures. Useful as a sanity check on a calculator run.
 */
export function severanceShareRange(): { low: number; high: number; median: number } {
  const shares = EASEMENT_VALUATION_CASES.filter(
    (entry) =>
      entry.severanceDamages !== null &&
      entry.totalAward !== null &&
      entry.totalAward > 0,
  )
    .map((entry) => (entry.severanceDamages as number) / (entry.totalAward as number))
    .sort((a, b) => a - b);

  if (shares.length === 0) return { low: 0, high: 0, median: 0 };
  const mid = Math.floor(shares.length / 2);
  const median =
    shares.length % 2 === 0 ? (shares[mid - 1]! + shares[mid]!) / 2 : shares[mid]!;
  return { low: shares[0]!, high: shares[shares.length - 1]!, median };
}
